import { useEffect, useState } from "react";
import { DEFAULT_API_PAYLOAD } from "~/lib/constants";
import { handleError } from "~/lib/error";
import { restApi } from "~/redux/restApi";

import AduseiEditor from "./editor";
import ExpandedEditor from "./expanded";
import { SuggestionProps } from "./suggestion";
import { restructureArrayToSuggestionProps } from "./type";

interface Props {
  onChange: (value: string) => void;
  defaultValue?: string;
  suggestions?: SuggestionProps[];
  title?: string;
  autoFocus?: boolean;
  placeholder?: string;
  className?: string;
  readOnly?: boolean;
}

const TheAduseiEditor = ({
  defaultValue,
  onChange,
  title,
  autoFocus,
  placeholder,
  className,
  readOnly,
  suggestions: suggestionsProp,
}: Props) => {
  const [loadSuggestions] = restApi.useGetSuggestionsMutation();
  const [editorValue, setEditorValue] = useState("");
  const [fetchedSuggestions, setFetchedSuggestions] = useState<
    SuggestionProps[]
  >([]);
  const [isOpen, setIsOpen] = useState(false);

  // Callers may supply their own suggestions; only self-fetch when they don't.
  // This prop was previously declared but never destructured, so every editor
  // instance refetched the whole link collection on mount regardless.
  const suggestions = suggestionsProp ?? fetchedSuggestions;

  // Add this useEffect to handle defaultValue changes
  useEffect(() => {
    if (defaultValue !== undefined) {
      setEditorValue(defaultValue);
    }
  }, [defaultValue]);

  useEffect(() => {
    if (suggestionsProp) return;
    handleLoadSuggestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestionsProp]);

  const handleLoadSuggestions = async () => {
    try {
      const response = await loadSuggestions({
        ...DEFAULT_API_PAYLOAD,
        getLinkCollectionRequest: { types: [] },
      }).unwrap();
      const data = response.result || [];
      setFetchedSuggestions(restructureArrayToSuggestionProps(data));
    } catch (error) {
      handleError(error);
    }
  };

  const handleOnChange = (value: string) => {
    setEditorValue(value);
    onChange(value); // Notify parent
  };

  return (
    <div className="w-full">
      <div>
        {!isOpen && (
          <AduseiEditor
            placeholder={placeholder}
            autoFocus={autoFocus}
            value={editorValue}
            onChange={handleOnChange}
            suggestions={suggestions}
            onExpand={() => setIsOpen(true)}
            isExpanded={isOpen}
            className={className}
            readOnly={readOnly}
          />
        )}
      </div>

      <ExpandedEditor
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title={title || ""}
        autoFocus={autoFocus}
        editorValue={editorValue}
        handleOnChange={handleOnChange} // Directly update main state
        suggestions={suggestions}
        readOnly={readOnly}
      />
    </div>
  );
};
export default TheAduseiEditor;
